import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Typography, Paper,
  TextField, Button, CircularProgress, InputAdornment,
  Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel,
  Dialog, DialogContent, DialogActions,
} from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import BusinessIcon from "@material-ui/icons/Business";
import SearchIcon from "@material-ui/icons/Search";
import { NumericFormat } from "react-number-format";
import {
  fetchCompanies, fetchCompanyUsers, fetchUserCredit, performCreditTransfer,
} from "../redux/credit-transfer.action";
import {
  setSelectedCompany, setSelectedUser, resetTransfer,
} from "../redux/credit-transfer.slice";

// Maximum quantity allowed for a single credit transfer.
const MAX_QUANTITY = 3000000;

const displayNameOf = (user) => user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim();

// Lets MUI's TextField render a thousands-separated numeric input.
// Stores the raw numeric string (e.g. "1000000"); displays "1,000,000".
// Blocks input above MAX_QUANTITY, and calls onMaxExceeded when the user tries
// to go over so the page can reveal the limit only at that moment.
function QuantityNumberFormat(props) {
  const { inputRef, onChange, onMaxExceeded, ...other } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({ target: { name: props.name, value: values.value } });
      }}
      isAllowed={({ floatValue }) => {
        if (floatValue !== undefined && floatValue > MAX_QUANTITY) {
          if (onMaxExceeded) onMaxExceeded();
          return false;
        }
        return true;
      }}
      thousandSeparator
      allowNegative={false}
      valueIsNumericString
    />
  );
}

const useStyles = makeStyles((theme) => ({
  pageHead: { marginBottom: 24 },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#101828",
    lineHeight: "30px",
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#667085",
    marginTop: 4,
  },

  stepCard: {
    border: "1px solid #E4E7EC",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
    marginBottom: 16,
    overflow: "hidden",
  },
  stepHead: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: "18px 24px",
    borderBottom: "1px solid #E4E7EC",
  },
  stepNum: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    backgroundColor: "#EEF2F3",
    color: "#3A4D54",
    border: "1.5px solid transparent",
  },
  stepNumDone: {
    backgroundColor: "#ECFDF3",
    color: "#067647",
    borderColor: "#ABEFC6",
  },
  stepTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#101828",
    lineHeight: "22px",
  },
  stepSub: {
    fontSize: 13,
    color: "#667085",
    marginTop: 1,
  },
  stepChip: {
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#EEF2F3",
    color: "#3A4D54",
    fontSize: 12.5,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 999,
    whiteSpace: "nowrap",
  },
  stepBody: { padding: "20px 24px 24px" },

  fieldLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    color: "#344054",
    marginBottom: 6,
  },
  requiredMark: { color: "#D92D20" },
  fieldHint: {
    fontSize: 12.5,
    color: "#667085",
    marginTop: 6,
  },
  companySelect: { minWidth: 360 },
  selectPlaceholder: { color: "#98A2B3" },

  tableHead: {
    "& .MuiTableCell-head": {
      fontSize: 11.5,
      fontWeight: 600,
      letterSpacing: 0.5,
      textTransform: "uppercase",
      color: "#667085",
      backgroundColor: "#FCFCFD",
      borderBottom: "1px solid #E4E7EC",
      padding: "10px 14px",
    },
  },
  tableRow: {
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: "#F9FAFB" },
    "& .MuiTableCell-body": {
      fontSize: 14,
      color: "#344054",
      padding: "12px 14px",
      borderBottom: "1px solid #F2F4F7",
    },
    "&:last-child .MuiTableCell-body": { borderBottom: "none" },
  },
  selectedRow: {
    backgroundColor: "#F3F7F8",
    borderLeftColor: "#3A4D54",
  },
  userName: {
    fontSize: 14,
    fontWeight: 500,
    color: "#101828",
    lineHeight: "20px",
  },
  userEmail: {
    fontSize: 12.5,
    color: "#667085",
    lineHeight: "18px",
    marginTop: 1,
  },
  typeBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
  },
  typeBadgeDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    display: "inline-block",
  },
  typeBadgeOwner: {
    backgroundColor: "#ECFDF3",
    color: "#067647",
    "& $typeBadgeDot": { backgroundColor: "#17B26A" },
  },
  typeBadgeEmployee: {
    backgroundColor: "#F2F4F7",
    color: "#344054",
    "& $typeBadgeDot": { backgroundColor: "#98A2B3" },
  },
  balanceValue: {
    fontSize: 14,
    fontWeight: 500,
    color: "#101828",
    fontVariantNumeric: "tabular-nums",
  },
  balanceZero: { color: "#98A2B3" },
  selectBtn: {
    fontSize: 13,
    fontWeight: 500,
    textTransform: "none",
    borderRadius: 8,
    padding: "5px 14px",
    minWidth: 96,
    border: "1px solid #D0D5DD",
    color: "#344054",
    "&:hover": { backgroundColor: "#F9FAFB", borderColor: "#98A2B3" },
  },
  selectBtnActive: {
    backgroundColor: "#3A4D54",
    borderColor: "#3A4D54",
    color: "#fff",
    "&:hover": { backgroundColor: "#2d3b41", borderColor: "#2d3b41" },
  },
  emptyState: {
    padding: "28px 24px",
    textAlign: "center",
    color: "#98A2B3",
    fontSize: 14,
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: "28px 24px",
  },
  tableToolbar: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "14px 24px 0",
  },
  searchField: {
    width: 260,
    "& .MuiOutlinedInput-input": { fontSize: 13.5 },
    "& .MuiInputAdornment-root .MuiSvgIcon-root": { fontSize: 18, color: "#98A2B3" },
  },

  transferGrid: {
    display: "grid",
    gridTemplateColumns: "1.25fr 1fr",
    gap: 24,
    alignItems: "start",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  formGroup: { marginBottom: 16 },
  quantityInput: {
    "& .MuiOutlinedInput-input": {
      fontSize: 16,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
    },
  },
  inputSuffix: {
    "& .MuiTypography-root": { fontSize: 13, color: "#98A2B3" },
  },

  summaryPanel: {
    backgroundColor: "#FBFCFC",
    border: "1px solid #E4E7EC",
    borderRadius: 12,
    padding: 20,
  },
  summaryHeading: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#667085",
    marginBottom: 14,
  },
  summaryRecipient: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    paddingBottom: 14,
    borderBottom: "1px solid #E4E7EC",
    marginBottom: 14,
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    padding: "5px 0",
    fontSize: 13.5,
  },
  summaryLabel: { color: "#667085", fontSize: 13.5 },
  summaryValue: {
    fontWeight: 500,
    color: "#101828",
    fontSize: 13.5,
    fontVariantNumeric: "tabular-nums",
  },
  summaryValuePlus: { color: "#067647" },
  summaryTotalRow: {
    borderTop: "1px dashed #D0D5DD",
    marginTop: 8,
    paddingTop: 12,
  },
  summaryTotalLabel: { color: "#344054", fontWeight: 500, fontSize: 13.5 },
  summaryTotalValue: {
    fontSize: 18,
    fontWeight: 700,
    color: "#101828",
    fontVariantNumeric: "tabular-nums",
  },

  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 20,
  },
  submitBtn: {
    backgroundColor: "#3A4D54",
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    padding: "9px 28px",
    borderRadius: 8,
    boxShadow: "0px 1px 2px rgba(16,24,40,0.05)",
    "&:hover": { backgroundColor: "#2d3b41" },
    "&:disabled": { backgroundColor: "#D0D5DD", color: "#98A2B3" },
  },

  confirmDialog: {
    "& .MuiDialog-paper": {
      borderRadius: 16,
      width: 440,
      maxWidth: "100%",
    },
  },
  dialogHead: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    padding: "22px 24px 0",
  },
  dialogIcon: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    flex: "none",
    backgroundColor: "#EEF2F3",
    color: "#3A4D54",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#101828",
  },
  dialogSubtitle: {
    fontSize: 13.5,
    color: "#667085",
    marginTop: 2,
  },
  receipt: {
    backgroundColor: "#FBFCFC",
    border: "1px solid #E4E7EC",
    borderRadius: 10,
    padding: "14px 16px",
  },
  dialogActions: {
    padding: "6px 24px 22px",
    "& > *": { flex: 1 },
  },
  cancelBtn: {
    color: "#344054",
    fontWeight: 500,
    fontSize: 14,
    textTransform: "none",
    border: "1px solid #D0D5DD",
    borderRadius: 8,
    padding: "9px 18px",
    "&:hover": { backgroundColor: "#F9FAFB" },
  },
  confirmBtn: {
    backgroundColor: "#3A4D54",
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    borderRadius: 8,
    padding: "9px 18px",
    "&:hover": { backgroundColor: "#2d3b41" },
  },
}));

function StepHeader({ classes, number, done, title, subtitle, chip }) {
  return (
    <Box className={classes.stepHead}>
      <Box className={`${classes.stepNum} ${done ? classes.stepNumDone : ""}`}>
        {done ? <CheckIcon style={{ fontSize: 16 }} /> : number}
      </Box>
      <Box>
        <Typography className={classes.stepTitle}>{title}</Typography>
        <Typography className={classes.stepSub}>{subtitle}</Typography>
      </Box>
      {chip}
    </Box>
  );
}

export default function CreditTransferPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    companies, selectedCompany, companyUsers, selectedUser,
    userCredit,
    isLoadingCompanies, isLoadingUsers, isLoadingCredit,
    isTransferring, transferResult, error,
  } = useSelector((state) => state.creditTransfer);

  const action = "credit";
  const [quantity, setQuantity] = useState("");
  const module = "serial_number";
  const [piPo, setPiPo] = useState("");
  const [remark, setRemark] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [maxWarnOpen, setMaxWarnOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [balanceOrder, setBalanceOrder] = useState(null); // null | 'desc' | 'asc'

  useEffect(() => {
    dispatch(fetchCompanies());
    return () => dispatch(resetTransfer());
  }, [dispatch]);

  // Deactivated companies stay in the store (transfer history still filters by
  // them) but are not offered as a transfer target.
  const selectableCompanies = useMemo(
    () => companies.filter((c) => c.company_status !== "deactivated"),
    [companies],
  );

  const handleCompanyChange = (event, company) => {
    dispatch(setSelectedCompany(company || null));
    setUserSearch("");
    setBalanceOrder(null);
    if (company) {
      dispatch(fetchCompanyUsers(company.company_uuid));
    }
  };

  const handleUserSelect = (user) => {
    dispatch(setSelectedUser(user));
    dispatch(fetchUserCredit(user.sub));
  };

  const visibleUsers = useMemo(() => {
    let list = companyUsers;
    const query = userSearch.trim().toLowerCase();
    if (query) {
      list = list.filter((u) => `${displayNameOf(u)} ${u.email || ""}`.toLowerCase().includes(query));
    }
    if (balanceOrder) {
      list = [...list].sort((a, b) => {
        const diff = parseFloat(a.balance ?? 0) - parseFloat(b.balance ?? 0);
        return balanceOrder === "asc" ? diff : -diff;
      });
    }
    return list;
  }, [companyUsers, userSearch, balanceOrder]);

  const handleSubmit = async () => {
    if (!selectedCompany || !selectedUser || !quantity) return;

    setConfirmOpen(false);

    const data = {
      company_id: selectedCompany.company_id,
      company_uuid: selectedCompany.company_uuid,
      user_sub: selectedUser.sub,
      user_id: selectedUser.id,
      action,
      amount: parseFloat(quantity),
      module,
      pi_no: piPo,
      remark,
    };

    const result = await dispatch(performCreditTransfer(data));
    setSnackOpen(true);
    if (result.success) {
      setQuantity("");
      setPiPo("");
      setRemark("");
      dispatch(fetchUserCredit(selectedUser.sub));
    }
  };

  const companyName = selectedCompany
    ? selectedCompany.company_name || `Company #${selectedCompany.company_id}`
    : "";

  const currentBalance = userCredit?.amount !== undefined ? parseFloat(userCredit.amount) : null;
  const transferAmount = parseFloat(quantity || 0);
  const newBalance = currentBalance !== null ? currentBalance + transferAmount : null;

  return (
    <Box>
      <Box className={classes.pageHead}>
        <Typography className={classes.pageTitle}>Credit Transfer</Typography>
        <Typography className={classes.pageSubtitle}>
          Top up serial-number credits for a user in one of your assigned companies.
        </Typography>
      </Box>

      {/* Step 1: Select Company */}
      <Paper className={classes.stepCard} elevation={0}>
        <StepHeader
          classes={classes}
          number="1"
          done={!!selectedCompany}
          title="Select company"
          subtitle="Choose which company the recipient belongs to"
          chip={selectedCompany && (
            <span className={classes.stepChip}>
              <BusinessIcon style={{ fontSize: 14 }} />
              {companyName}
            </span>
          )}
        />
        <Box className={classes.stepBody}>
          <label className={classes.fieldLabel} htmlFor="company-select">
            Company <span className={classes.requiredMark}>*</span>
          </label>
          <Box display="flex" alignItems="center">
            <Autocomplete
              className={classes.companySelect}
              options={selectableCompanies}
              getOptionLabel={(c) => c.company_name || `Company #${c.company_id}`}
              getOptionSelected={(option, value) => option.company_uuid === value.company_uuid}
              value={selectedCompany || null}
              onChange={handleCompanyChange}
              size="small"
              noOptionsText="No companies found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  id="company-select"
                  variant="outlined"
                  placeholder="Search or select company"
                />
              )}
            />
            {isLoadingCompanies && <CircularProgress size={20} style={{ marginLeft: 16 }} />}
          </Box>
        </Box>
      </Paper>

      {/* Step 2: Select User */}
      <Paper className={classes.stepCard} elevation={0}>
        <StepHeader
          classes={classes}
          number="2"
          done={!!selectedUser}
          title="Select recipient"
          subtitle={!selectedCompany
            ? "Waiting for company selection"
            : isLoadingUsers
              ? "Loading users…"
              : `${companyUsers.length} ${companyUsers.length === 1 ? "user" : "users"} in ${companyName}`}
          chip={selectedUser && (
            <span className={classes.stepChip}>{displayNameOf(selectedUser)}</span>
          )}
        />
        {!selectedCompany ? (
          <Typography className={classes.emptyState}>Select a company above to load its users.</Typography>
        ) : isLoadingUsers ? (
            <Box className={classes.loadingBox}>
              <CircularProgress size={24} />
            </Box>
          ) : companyUsers.length === 0 ? (
            <Typography className={classes.emptyState}>No users found in this company</Typography>
          ) : (
            <>
            <Box className={classes.tableToolbar}>
              <TextField
                variant="outlined" size="small"
                placeholder="Search name or email"
                className={classes.searchField}
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell style={{ paddingLeft: 24 }}>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={!!balanceOrder}
                        direction={balanceOrder || "desc"}
                        onClick={() => setBalanceOrder((order) => (order === "desc" ? "asc" : order === "asc" ? null : "desc"))}
                      >
                        Balance
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" style={{ paddingRight: 24 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} style={{ textAlign: "center", color: "#98A2B3", padding: "24px 14px" }}>
                        No users match your search
                      </TableCell>
                    </TableRow>
                  )}
                  {visibleUsers.map((user) => {
                    const isSelected = selectedUser?.id === user.id;
                    const name = displayNameOf(user);
                    const balance = parseFloat(user.balance ?? 0);
                    return (
                      <TableRow
                        key={user.id}
                        className={`${classes.tableRow} ${isSelected ? classes.selectedRow : ""}`}
                        onClick={() => handleUserSelect(user)}
                      >
                        <TableCell style={{ paddingLeft: 24 }}>
                          <Box>
                            <Typography className={classes.userName}>{name}</Typography>
                            <Typography className={classes.userEmail}>{user.email}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`${classes.typeBadge} ${user.user_type === "Owner" ? classes.typeBadgeOwner : classes.typeBadgeEmployee}`}
                          >
                            <span className={classes.typeBadgeDot} />
                            {user.user_type || "Employee"}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <span className={`${classes.balanceValue} ${balance === 0 ? classes.balanceZero : ""}`}>
                            {balance.toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell align="right" style={{ paddingRight: 24 }}>
                          <Button
                            size="small"
                            className={`${classes.selectBtn} ${isSelected ? classes.selectBtnActive : ""}`}
                            onClick={() => handleUserSelect(user)}
                            startIcon={isSelected ? <CheckIcon style={{ fontSize: 14 }} /> : null}
                            disableElevation
                          >
                            {isSelected ? "Selected" : "Select"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
      </Paper>

      {/* Step 3: Credit Balance & Transfer */}
      <Paper className={classes.stepCard} elevation={0}>
        <StepHeader
          classes={classes}
          number="3"
          done={false}
          title="Transfer credits"
          subtitle={selectedUser
            ? "Enter the amount to credit the selected user"
            : "Waiting for recipient selection"}
        />
        {!selectedUser ? (
          <Typography className={classes.emptyState}>Select a recipient above to enter the transfer amount.</Typography>
        ) : (
          <Box className={classes.stepBody}>
            {isLoadingCredit ? (
              <Box className={classes.loadingBox}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                <Box className={classes.transferGrid}>
                  <Box>
                    <Box className={classes.formGroup}>
                      <label className={classes.fieldLabel} htmlFor="transfer-quantity">
                        Quantity <span className={classes.requiredMark}>*</span>
                      </label>
                      <TextField
                        id="transfer-quantity"
                        fullWidth variant="outlined" size="small"
                        placeholder="0"
                        className={classes.quantityInput}
                        value={quantity} onChange={(e) => setQuantity(e.target.value)}
                        InputProps={{
                          inputComponent: QuantityNumberFormat,
                          endAdornment: (
                            <InputAdornment position="end" className={classes.inputSuffix}>
                              credits
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{ onMaxExceeded: () => setMaxWarnOpen(true) }}
                      />
                    </Box>
                    <Box className={classes.formGroup}>
                      <label className={classes.fieldLabel} htmlFor="transfer-pipo">PI / PO</label>
                      <TextField
                        id="transfer-pipo"
                        fullWidth variant="outlined" size="small"
                        placeholder="e.g. PI-2026-0001"
                        value={piPo} onChange={(e) => setPiPo(e.target.value)}
                        inputProps={{ maxLength: 255 }}
                      />
                      <Typography className={classes.fieldHint}>
                        Optional — reference number for this top-up
                      </Typography>
                    </Box>
                    <Box className={classes.formGroup} style={{ marginBottom: 0 }}>
                      <label className={classes.fieldLabel} htmlFor="transfer-remark">Remark</label>
                      <TextField
                        id="transfer-remark"
                        fullWidth variant="outlined" size="small"
                        placeholder="Optional note for this transfer"
                        value={remark} onChange={(e) => setRemark(e.target.value)}
                        multiline minRows={3}
                      />
                    </Box>
                  </Box>

                  <Box className={classes.summaryPanel}>
                    <Typography className={classes.summaryHeading}>Transfer summary</Typography>
                    <Box className={classes.summaryRecipient}>
                      <Box>
                        <Typography className={classes.userName}>{displayNameOf(selectedUser)}</Typography>
                        <Typography className={classes.userEmail}>{selectedUser.email}</Typography>
                      </Box>
                    </Box>
                    <Box className={classes.summaryRow}>
                      <span className={classes.summaryLabel}>Current balance</span>
                      <span className={classes.summaryValue}>
                        {currentBalance !== null ? currentBalance.toLocaleString() : "N/A"}
                      </span>
                    </Box>
                    <Box className={classes.summaryRow}>
                      <span className={classes.summaryLabel}>Transfer amount</span>
                      <span className={`${classes.summaryValue} ${classes.summaryValuePlus}`}>
                        {transferAmount > 0 ? `+ ${transferAmount.toLocaleString()}` : "—"}
                      </span>
                    </Box>
                    <Box className={`${classes.summaryRow} ${classes.summaryTotalRow}`}>
                      <span className={classes.summaryTotalLabel}>New balance</span>
                      <span className={classes.summaryTotalValue}>
                        {newBalance !== null ? newBalance.toLocaleString() : "N/A"}
                      </span>
                    </Box>
                  </Box>
                </Box>

                <Box className={classes.actions}>
                  <Button
                    className={classes.submitBtn}
                    onClick={() => setConfirmOpen(true)}
                    disabled={isTransferring || !quantity}
                    endIcon={!isTransferring ? <ArrowForwardIcon style={{ fontSize: 16 }} /> : null}
                    disableElevation
                  >
                    {isTransferring ? (
                      <CircularProgress size={20} style={{ color: "#fff" }} />
                    ) : (
                      "Submit transfer"
                    )}
                  </Button>
                </Box>

                <Dialog
                  open={confirmOpen}
                  onClose={() => setConfirmOpen(false)}
                  className={classes.confirmDialog}
                  aria-labelledby="confirm-transfer-title"
                >
                  <Box className={classes.dialogHead}>
                    <Box className={classes.dialogIcon}>
                      <SwapHorizIcon style={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography id="confirm-transfer-title" className={classes.dialogTitle}>
                        Confirm credit transfer
                      </Typography>
                      <Typography className={classes.dialogSubtitle}>
                        Please review the details before confirming.
                      </Typography>
                    </Box>
                  </Box>
                  <DialogContent style={{ padding: "18px 24px" }}>
                    <Box className={classes.receipt}>
                      <Box className={classes.summaryRow}>
                        <span className={classes.summaryLabel}>Company</span>
                        <span className={classes.summaryValue}>{companyName}</span>
                      </Box>
                      <Box className={classes.summaryRow}>
                        <span className={classes.summaryLabel}>Recipient</span>
                        <span className={classes.summaryValue}>
                          {displayNameOf(selectedUser)} ({selectedUser.user_type || "Employee"})
                        </span>
                      </Box>
                      <Box className={classes.summaryRow}>
                        <span className={classes.summaryLabel}>Transfer amount</span>
                        <span className={`${classes.summaryValue} ${classes.summaryValuePlus}`}>
                          {`+ ${parseFloat(quantity || 0).toLocaleString()}`}
                        </span>
                      </Box>
                      {piPo.trim() && (
                        <Box className={classes.summaryRow}>
                          <span className={classes.summaryLabel}>PI / PO</span>
                          <span className={classes.summaryValue}>{piPo}</span>
                        </Box>
                      )}
                      {remark.trim() && (
                        <Box className={classes.summaryRow}>
                          <span className={classes.summaryLabel}>Remark</span>
                          <span
                            className={classes.summaryValue}
                            style={{ textAlign: "right", maxWidth: "62%", wordBreak: "break-word" }}
                          >
                            {remark}
                          </span>
                        </Box>
                      )}
                      <Box className={`${classes.summaryRow} ${classes.summaryTotalRow}`}>
                        <span className={classes.summaryTotalLabel}>New balance</span>
                        <span className={classes.summaryTotalValue}>
                          {newBalance !== null ? newBalance.toLocaleString() : "N/A"}
                        </span>
                      </Box>
                    </Box>
                  </DialogContent>
                  <DialogActions className={classes.dialogActions}>
                    <Button onClick={() => setConfirmOpen(false)} className={classes.cancelBtn}>
                      Cancel
                    </Button>
                    <Button onClick={handleSubmit} className={classes.confirmBtn} variant="contained" disableElevation>
                      Confirm transfer
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            )}
          </Box>
        )}
      </Paper>

      <Snackbar
        open={maxWarnOpen}
        autoHideDuration={4000}
        onClose={() => setMaxWarnOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setMaxWarnOpen(false)}>
          Maximum quantity is {MAX_QUANTITY.toLocaleString()}
        </Alert>
      </Snackbar>

      <Snackbar open={snackOpen} autoHideDuration={5000} onClose={() => setSnackOpen(false)}>
        <Alert severity={transferResult?.success ? "success" : "error"} onClose={() => setSnackOpen(false)}>
          {transferResult?.success ? "Credit transfer completed successfully!" : transferResult?.message || "Transfer failed"}
        </Alert>
      </Snackbar>

      {error && (
        <Snackbar open={!!error} autoHideDuration={5000}>
          <Alert severity="error">{error}</Alert>
        </Snackbar>
      )}
    </Box>
  );
}
